import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { StyleRoot, useTokenData } from "../../../global-account/react/index.js";
import invariant from "../../../shared/utils/debug.js";
import { useMemo } from "react";
import { encodeFunctionData, parseEther } from "viem";
import { ABI_SIGNATURE_MINTING } from "../../abis/signatureMinting.js";
import { AnySpendCustom } from "./AnySpendCustom.js";
// Helper function to determine if URL is a video
function isVideoURL(url) {
  const videoExtensions = [".mp4", ".webm", ".ogg"];
  return videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
}
function generateEncodedDataForSignatureMint(signatureData) {
  const {
    signature,
    payload
  } = signatureData;
  invariant(signature, "Signature is required");
  invariant(payload, "Payload is required");
  const mintRequest = {
    to: payload.to,
    royaltyRecipient: payload.royaltyRecipient,
    royaltyBps: BigInt(payload.royaltyBps || 0),
    primarySaleRecipient: payload.primarySaleRecipient,
    tokenId: BigInt(payload.tokenId || 0),
    uri: payload.uri,
    quantity: BigInt(payload.quantity || 1),
    pricePerToken: parseEther(payload.price?.toString() || "0"),
    currency: payload.currencyAddress,
    validityStartTimestamp: BigInt(payload.mintStartTime || 0),
    validityEndTimestamp: BigInt(payload.mintEndTime || 0),
    uid: payload.uid
  };
  const encodedData = encodeFunctionData({
    abi: [ABI_SIGNATURE_MINTING[0]],
    functionName: "mintWithSignature",
    args: [mintRequest, signature]
  });
  return encodedData;
}
export function AnyspendSignatureMint({
  loadOrder,
  mode = "modal",
  signatureData,
  imageUrl,
  onSuccess,
  showFiatOption,
  slots,
  content,
  theme
}) {
  // Get token data
  const {
    data: tokenData,
    isError: isTokenError,
    isLoading
  } = useTokenData(signatureData.collection.chainId, signatureData.collection.signatureRequestBody?.currency);
  // Convert token data to AnySpend Token type
  const dstToken = useMemo(() => {
    if (!tokenData) return null;
    return {
      address: tokenData.address,
      chainId: signatureData.collection.chainId,
      name: tokenData.name,
      symbol: tokenData.symbol,
      decimals: tokenData.decimals,
      metadata: {
        logoURI: tokenData.logoURI
      }
    };
  }, [tokenData, signatureData.collection.chainId]);
  const mediaUrl = imageUrl || signatureData.payload.metadata.image || "https://cdn.b3.fun/nft-placeholder.png";
  const isVideo = isVideoURL(mediaUrl);
  const header = () => _jsxs(_Fragment, {
    children: [_jsx("div", {
      className: "relative mx-auto size-32",
      children: isVideo ? _jsxs("video", {
        autoPlay: true,
        loop: true,
        muted: true,
        playsInline: true,
        className: "size-full rounded-lg object-cover",
        children: [_jsx("source", {
          src: mediaUrl,
          type: `video/${mediaUrl.split(".").pop()}`
        }), "Your browser does not support the video tag."]
      }) : _jsx("img", {
        alt: "nft preview",
        className: "size-full rounded-lg object-cover",
        src: mediaUrl
      })
    }), _jsxs("div", {
      className: "mt-[-60px] w-full rounded-t-lg bg-white",
      children: [_jsx("div", {
        className: "h-[60px] w-full"
      }), _jsx("div", {
        className: "mb-1 flex w-full flex-col items-center gap-2 p-5",
        children: _jsxs("span", {
          className: "font-sf-rounded text-2xl font-semibold",
          children: [signatureData.payload.metadata.name || "Mint NFT", " ", Number(signatureData.payload.quantity) > 1 ? `(${signatureData.payload.quantity}x)` : ""]
        })
      })]
    })]
  });
  // Show loading state while fetching token data
  if (isLoading) {
    return _jsx(StyleRoot, {
      children: _jsx("div", {
        className: "b3-root b3-modal bg-b3-react-background flex w-full flex-col items-center p-8",
        children: _jsx("p", {
          className: "text-as-primary/70 text-center text-sm",
          children: "Loading payment token information..."
        })
      })
    });
  }
  // If we don't have token data after loading, show error state
  if (!dstToken || isTokenError) {
    return _jsx(StyleRoot, {
      children: _jsx("div", {
        className: "b3-root b3-modal bg-b3-react-background flex w-full flex-col items-center p-8",
        children: _jsxs("p", {
          className: "text-as-red text-center text-sm",
          children: ["Failed to fetch payment token information for ", signatureData.collection.signatureRequestBody?.currency, " on chain ", signatureData.collection.chainId, ". Please try again."]
        })
      })
    });
  }
  const encodedData = generateEncodedDataForSignatureMint(signatureData);
  const price = parseEther(signatureData.payload.price?.toString() || "0");
  return _jsx(AnySpendCustom, {
    loadOrder: loadOrder,
    mode: mode,
    recipientAddress: signatureData.payload.to,
    orderType: "custom",
    dstChainId: signatureData.collection.chainId,
    dstToken: dstToken,
    dstAmount: price.toString(),
    contractAddress: signatureData.collection.address || "",
    encodedData: encodedData,
    metadata: {
      action: "Signature Mint"
    },
    header: header,
    onSuccess: onSuccess,
    showRecipient: true,
    showFiatOption: showFiatOption,
    slots: slots,
    content: content,
    theme: theme
  });
}